import { useState, useCallback } from 'react';
import { BrowserProvider } from 'ethers';
import { getNonce, verifySignature } from '../services/api.js';
import { trackEvent } from '../lib/analytics.js';

interface WalletState {
  address: string | null;
  token: string | null;
  isConnected: boolean;
  isConnecting: boolean;
  error: string | null;
}

const TOKEN_KEY = 'snap_auth_token';
const WALLET_KEY = 'snap_wallet_address';

export function useWallet() {
  const [state, setState] = useState<WalletState>(() => {
    const token = localStorage.getItem(TOKEN_KEY);
    const address = localStorage.getItem(WALLET_KEY);
    return {
      address,
      token,
      isConnected: !!token && !!address,
      isConnecting: false,
      error: null,
    };
  });

  const connect = useCallback(async () => {
    setState((s) => ({ ...s, isConnecting: true, error: null }));

    try {
      if (!window.ethereum) {
        throw new Error('MetaMask is not installed.');
      }

      const provider = new BrowserProvider(window.ethereum);
      await provider.send('eth_requestAccounts', []);
      const signer = await provider.getSigner();
      const address = await signer.getAddress();

      if (!address) {
        throw new Error('Wallet connection failed');
      }

      const { nonce } = await getNonce();

      const domain = window.location.host;
      const origin = window.location.origin;
      const issuedAt = new Date().toISOString();

      const message = [
        `${domain} wants you to sign in with your Ethereum account:`,
        address,
        '',
        'Sign in to CryptoGuardians',
        '',
        `URI: ${origin}`,
        'Version: 1',
        'Chain ID: 1',
        `Nonce: ${nonce}`,
        `Issued At: ${issuedAt}`,
      ].join('\n');

      const signature = await signer.signMessage(message);
      const result = await verifySignature(address, message, signature);

      localStorage.setItem(TOKEN_KEY, result.token);
      localStorage.setItem(WALLET_KEY, result.walletAddress);

      trackEvent('wallet_connected', { address: result.walletAddress });

      setState({
        address: result.walletAddress,
        token: result.token,
        isConnected: true,
        isConnecting: false,
        error: null,
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to connect wallet';
      setState((s) => ({ ...s, isConnecting: false, error: message }));
    }
  }, []);

  return { ...state, connect };
}
